/**
 * ServiceManager — registre de services par instance HAObject (ESM, ha/441a).
 *
 * Port du leaf différé par ha/440 (codex finding t.2). Recette §1 + §3.1
 * appliquées :
 *  - §1.1 : wrapper `namespace HA { … }` retiré, exports ESM top-level.
 *  - §3.1 : cycle ESM `HAObject ↔ ServiceManager` cassé via
 *    `registerServiceManager(ServiceManager)` au top-level. HAObject expose le
 *    slot `_ServiceManagerCtor` (déjà posé en ha/440) ; ce module le remplit
 *    par side-effect d'import.
 *
 * Effet runtime : tout module ESM qui importe transitivement
 * `Core/ServiceManager` peuple le slot AVANT le premier
 * `new HAObject({services:…})`. Le wrap `services → ServiceManager` de
 * `HAObject.initialize` (dormant pendant ha/440) est ainsi **réactivé**.
 */

import { HAObject, registerServiceManager } from "./HAObject";

export class ServiceManager extends HAObject {

    public addServices(services: Map<string, () => void>): void {
        services.forEach((func, name) => {
            this.addService(name, func);
        });
    }

    public addService(serviceName: string, func: () => void): void {
        this.__getService().set(serviceName, func);
    }

    public getServices(names: string[]): Map<string, () => void> {
        const result: Map<string, () => void> = new Map();
        names.forEach((name) => {
            result.set(name, this.getService(name));
        });
        return result;
    }

    public getService(name: string): () => void {
        return this.__getService().get(name);
    }

    protected initialize(data?: Map<string, unknown>): void {
        if (data) {
            super.initialize({ __services: data });
        }
    }

    protected __getService(): Map<string, () => void> {
        return (this as unknown as { get(k: string): Map<string, () => void> }).get("__services");
    }
}

// Side-effect top-level (recette §3.1) : remplit le slot exposé par HAObject.
// AVANT ce point, `HAObject.initialize` avait son wrap services DORMANT (ha/440).
// APRÈS, tout consumer ESM qui charge cette chaîne voit le wrap reprendre.
registerServiceManager(ServiceManager);
